import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { Field } from 'redux-form';
import { Icon, Box, LoaderDots, Button, Text, Spacing } from '@reservamos/elements';
import { trackEvent } from 'user-analytics';
import { fieldRenderer } from 'utils/formRenderers';
import { hasCdsAccount, isCdsCouponBlocked } from 'utils/purchase/cdsPointsRecovery';
import getPurchaseStepName from '../../../utils/purchase/getPurchaseStepName';
import 'styles/components/LoadingDots';

/**
 * Tracks promotional code application result
 * @param {string} promoCode - The promotional code that was applied
 * @param {boolean} success - Whether the application was successful
 * @param {string} [error] - Error message if the application failed
 * @param {string} token - The purchase token
 */
const trackPromoCodeResult = (promoCode, success, error = null, token) => {
  trackEvent('Promotional Code Applied', {
    Page: 'Checkout',
    Action: 'Apply Promo Code',
    Success: success,
    PromoCode: promoCode,
    PurchaseToken: token,
    ...(success === false && { Error: error || 'Unknown error' }),
  });
};

const propTypes = {
  token: PropTypes.string.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  onApplyPromo: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  features: PropTypes.object.isRequired,
  promoCodeApplied: PropTypes.bool,
  cdsPointsBlocked: PropTypes.bool,
  promoCodeSubmitting: PropTypes.bool,
};

/**
 * PromotionalCode component.
 * Renders a form section for applying promotional codes.
 * @param {object} props - token, handleSubmit, onApplyPromo, t, features
 * @param {string} props.token - The purchase token
 * @param {function} props.handleSubmit - Redux form handleSubmit function
 * @param {function} props.onApplyPromo - Function to apply the promotional code
 * @param {function} props.t - Translation function
 * @param {object} props.features - Features object
 * @param {boolean} props.promoCodeApplied - Whether a promo code has been successfully applied
 */
class PromotionalCode extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: false,
    };
    this.applyPromo = this.applyPromo.bind(this);
  }

  applyPromo({ promoCode }) {
    const { token, onApplyPromo, cdsPointsBlocked, promoCodeSubmitting } = this.props;
    const { isLoading } = this.state;
    if (cdsPointsBlocked || promoCodeSubmitting || isLoading) return;
    this.setState({ isLoading: true });

    onApplyPromo(token, promoCode)
      .then((result) => {
        trackPromoCodeResult(
          promoCode,
          result?.success || false,
          result?.success === false ? result?.message : null,
          token,
        );
      })
      .catch((error) => {
        let errorMessage = typeof error === 'string' ? error : error?.message;
        errorMessage = errorMessage || 'Unknown error';
        trackPromoCodeResult(promoCode, false, errorMessage, token);
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  }

  render() {
    const { handleSubmit, t, features, promoCodeApplied, cdsPointsBlocked, promoCodeSubmitting } =
      this.props;
    const { isLoading } = this.state;
    const disabled = cdsPointsBlocked || promoCodeSubmitting || isLoading;

    const purchaseStep = getPurchaseStepName(window.location.pathname);
    if (purchaseStep !== 'checkout') return null;

    if (!features.PROMOTIONAL_CODE_ENABLED) return null;

    return (
      <Box
        alphaBg="M"
        bgColor="grayBorder"
        borderRadius="M"
        paddingHorizontal="M"
        paddingVertical="M"
      >
        <Spacing size="S" vertical>
          <Text size="XL" weight="bold">
            {t('codes.title')}
          </Text>

          {promoCodeApplied ? (
            <Box
              alphaBg="S"
              bgColor="success"
              border="all"
              borderColor="success"
              borderRadius="M"
              paddingHorizontal="S"
              paddingVertical="S"
            >
              <Spacing size="S" alignItems="center" justifyContent="center">
                <Text size="M" weight="bold" color="success">
                  {t('codes.success')}
                </Text>
                <Icon color="success" type="CircleCheckmark" />
              </Spacing>
            </Box>
          ) : (
            <>
              <Spacing vertical>
                <Text>{t('codes.description')}</Text>
                <form
                  className="form-item-wrap"
                  aria-label={t('codes.title')}
                  onSubmit={handleSubmit(this.applyPromo)}
                >
                  <div className="form-item-wrap-l">
                    <Field
                      type="text"
                      name="promoCode"
                      id="promoCodeSidebar"
                      placeholder={t('codes.placeholder')}
                      placeholderOnly
                      cls="form-item textfield"
                      component={fieldRenderer}
                      isDisabled={disabled}
                    />
                  </div>

                  <div className="form-item-wrap-button">
                    {isLoading ? (
                      <LoaderDots isAccent />
                    ) : (
                      <Button
                        variant="primary"
                        withHeight
                        text={t('codes.button')}
                        buttonType="submit"
                        isDisabled={disabled}
                      />
                    )}
                  </div>
                </form>
              </Spacing>
            </>
          )}
        </Spacing>
      </Box>
    );
  }
}

PromotionalCode.propTypes = propTypes;

const mapStateToProps = (state) => ({
  features: state.whitelabelConfig.features,
  promoCodeApplied: Boolean(
    state.purchase.get('promoCodeApplied') || state.purchase.get('promoTravelerCode')?.trim(),
  ),
  promoCodeSubmitting: Boolean(state.purchase.get('promoCodeSubmitting')),
  cdsPointsBlocked: isCdsCouponBlocked(
    {
      token: state.purchase.get('token'),
      cdsPointsEnabled: state.purchase.get('cdsPointsEnabled'),
      cdsPointsPreview: state.purchase.get('cdsPointsPreview'),
      cdsPointsRedemptionId: state.purchase.get('cdsPointsRedemptionId'),
      cdsPointsRedemption: state.purchase.get('cdsPointsRedemption')
        ? { status: state.purchase.getIn(['cdsPointsRedemption', 'status']) }
        : null,
    },
    hasCdsAccount(state.whitelabelConfig.env),
  ),
});

export default connect(mapStateToProps)(withTranslation('purchase')(PromotionalCode));
